import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api.js";
import type { WritingStats } from "../lib/types.js";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import { formatNumber } from "../lib/format.js";

interface Props {
  projectId: string;
  reloadKey?: number;
}

/**
 * Daily writing goal, streaks, and a 30-day activity chart. Distinct from the
 * project-level target word count: this tracks per-day momentum. The goal is
 * editable inline and persisted to project settings.
 */
export function WritingGoalCard({ projectId, reloadKey }: Props) {
  const toast = useToast();
  const [stats, setStats] = useState<WritingStats | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setStats(await api.writingStats(projectId));
    } catch {
      /* non-fatal: the card just stays hidden/empty */
    }
  }, [projectId]);

  useEffect(() => { void load(); }, [load, reloadKey]);

  const saveGoal = async () => {
    const value = Math.max(0, Math.min(100000, Math.round(Number(draft) || 0)));
    setSaving(true);
    try {
      await api.updateSettings(projectId, { dailyWordGoal: value });
      setEditing(false);
      await load();
      toast.success(value > 0 ? `Daily goal set to ${formatNumber(value)} words.` : "Daily goal cleared.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't save the goal");
    } finally {
      setSaving(false);
    }
  };

  const maxBar = useMemo(() => {
    if (!stats) return 1;
    return Math.max(stats.today.goal, ...stats.recent.map((r) => r.wordsAdded), 1);
  }, [stats]);

  if (!stats) return null;

  const { today, streak, recent } = stats;
  const pct = today.goal > 0 ? Math.min(100, Math.round((today.wordsAdded / today.goal) * 100)) : 0;

  return (
    <section className="card pad wg-card">
      <div className="row gap-2" style={{ marginBottom: 14 }}>
        <Icon name="target" size={17} style={{ color: "var(--accent)" }} />
        <h3>Daily goal &amp; streak</h3>
        <span className="spacer" />
        {streak.current > 0 && (
          <span className="wg-streak" title={`Longest streak: ${streak.longest} day${streak.longest === 1 ? "" : "s"}`}>
            🔥 {streak.current} day{streak.current === 1 ? "" : "s"}
          </span>
        )}
      </div>

      {today.goal > 0 && !editing ? (
        <>
          <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
            <span className="wg-today">
              <span className="wg-today-num">{formatNumber(today.wordsAdded)}</span>
              <span className="muted"> / {formatNumber(today.goal)} words today</span>
            </span>
            <span className="row gap-2" style={{ alignItems: "center" }}>
              {today.goalMet && <span className="chip chip-accent"><Icon name="check" size={12} /> Goal met</span>}
              <button className="btn btn-icon btn-ghost" title="Edit daily goal" onClick={() => { setDraft(String(today.goal)); setEditing(true); }}>
                <Icon name="edit" size={14} />
              </button>
            </span>
          </div>
          <div className="progress-track">
            <div className="progress-fill" style={{ width: `${pct}%` }} />
          </div>
          <div className="row" style={{ justifyContent: "space-between", marginTop: 6 }}>
            <span className="faint" style={{ fontSize: 11.5 }}>{pct}% of today's goal</span>
            <span className="faint" style={{ fontSize: 11.5 }}>Longest streak: {streak.longest} day{streak.longest === 1 ? "" : "s"}</span>
          </div>
        </>
      ) : (
        <div className="wg-edit">
          <p className="muted" style={{ margin: "0 0 10px" }}>
            {today.goal > 0 ? "Update your daily word goal." : "Set a daily word goal to build a writing streak."}
          </p>
          <div className="row gap-2">
            <input
              className="input"
              type="number"
              min={0}
              max={100000}
              step={50}
              placeholder="e.g. 500"
              value={draft}
              autoFocus
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") void saveGoal(); }}
              style={{ maxWidth: 160 }}
            />
            <button className="btn btn-sm btn-primary" onClick={() => void saveGoal()} disabled={saving}>
              {saving ? "Saving…" : "Save goal"}
            </button>
            {editing && today.goal > 0 && (
              <button className="btn btn-sm btn-ghost" onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
            )}
          </div>
        </div>
      )}

      {/* 30-day activity chart */}
      <div className="wg-chart" role="img" aria-label="Words written over the last 30 days">
        {recent.map((r) => {
          const h = Math.round((r.wordsAdded / maxBar) * 100);
          const met = r.goal > 0 && r.wordsAdded >= r.goal;
          const isToday = r.day === today.day;
          return (
            <div
              key={r.day}
              className={`wg-bar ${met ? "wg-bar--met" : ""} ${isToday ? "wg-bar--today" : ""}`}
              style={{ height: `${Math.max(r.wordsAdded > 0 ? 4 : 1, h)}%` }}
              title={`${r.day}: ${formatNumber(r.wordsAdded)} words`}
            />
          );
        })}
      </div>
      <div className="row" style={{ justifyContent: "space-between", marginTop: 4 }}>
        <span className="faint" style={{ fontSize: 11 }}>30 days ago</span>
        <span className="faint" style={{ fontSize: 11 }}>Today</span>
      </div>
    </section>
  );
}
